import { useCallback, useEffect, useState } from "react";
import Navbar from "../components/sections/Navbar";
import Breadcrumb from "../components/ui/Breadcrumb";
import PageHeader from "../components/sections/PageHeader";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { useMessages } from "../context/MessageContext";
import "./DashboardManagement.css";

export default function DashboardManagement() {
  const { authorizedFetch } = useAuth();
  const { addMessage } = useMessages();
  const [onderzoeken, setOnderzoeken] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await authorizedFetch("/api/onderzoek-management/");
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || "Onderzoeksrechten konden niet worden geladen.");
      }
      const nextOnderzoeken = payload?.onderzoeken || [];
      setOnderzoeken(nextOnderzoeken);
      setRegions(payload?.regions || []);
      setSelections(
        Object.fromEntries(
          nextOnderzoeken.map((onderzoek) => [onderzoek.id, onderzoek.region_ids || []])
        )
      );
    } catch (err) {
      setError(err.message || "Onderzoeksrechten konden niet worden geladen.");
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleRegion = (onderzoekId, regionId) => {
    setSelections((current) => {
      const selected = current[onderzoekId] || [];
      return {
        ...current,
        [onderzoekId]: selected.includes(regionId)
          ? selected.filter((id) => id !== regionId)
          : [...selected, regionId],
      };
    });
  };

  const saveOnderzoek = async (onderzoek) => {
    setSavingId(onderzoek.id);
    setError("");
    try {
      const response = await authorizedFetch(
        `/api/onderzoek-management/${onderzoek.id}/`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ region_ids: selections[onderzoek.id] || [] }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || "Onderzoeksrechten konden niet worden opgeslagen.");
      }
      setOnderzoeken((current) =>
        current.map((item) => (item.id === onderzoek.id ? payload : item))
      );
      addMessage("success", `Toegang voor ${onderzoek.name} is opgeslagen.`);
    } catch (err) {
      setError(err.message || "Onderzoeksrechten konden niet worden opgeslagen.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <Navbar />
      <main className="container dashboard-management">
        <Breadcrumb icon="home-line.png" items={[]} current="Onderzoek beheer" />
        <PageHeader title="Onderzoek beheer" />
        <p className="dashboard-management__intro">
          Kies per onderzoek welke actieve regio&apos;s toegang hebben. Superadmins
          houden altijd toegang.
        </p>

        {error && <p className="dashboard-management__error">{error}</p>}
        {loading && <p>Onderzoeksrechten laden…</p>}

        {!loading && onderzoeken.length === 0 && (
          <p>Er zijn nog geen onderzoeken geregistreerd.</p>
        )}

        <div className="dashboard-management__grid">
          {onderzoeken.map((onderzoek) => (
            <section className="dashboard-management__card" key={onderzoek.id}>
              <div className="dashboard-management__heading">
                <div>
                  <h2>{onderzoek.name}</h2>
                  <span>{onderzoek.dashboard_route}</span>
                </div>
                <span className={`dashboard-management__status ${onderzoek.is_active ? "is-active" : ""}`}>
                  {onderzoek.is_active ? "Actief" : "Inactief"}
                </span>
              </div>

              <div className="dashboard-management__questionnaires">
                <strong>Vragenlijsten</strong>
                {onderzoek.vragenlijsten?.length ? (
                  <ul>
                    {onderzoek.vragenlijsten.map((vragenlijst) => (
                      <li key={vragenlijst.id}>{vragenlijst.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p>Geen vragenlijsten gekoppeld.</p>
                )}
              </div>

              <fieldset disabled={!onderzoek.is_active || savingId === onderzoek.id}>
                <legend>Regio&apos;s met toegang</legend>
                {regions.length === 0 ? (
                  <p>Er zijn geen actieve regio&apos;s.</p>
                ) : (
                  <div className="dashboard-management__regions">
                    {regions.map((region) => (
                      <label key={region.id}>
                        <input
                          type="checkbox"
                          checked={(selections[onderzoek.id] || []).includes(region.id)}
                          onChange={() => toggleRegion(onderzoek.id, region.id)}
                        />
                        <span>{region.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>

              <div className="dashboard-management__actions">
                <Button
                  onClick={() => saveOnderzoek(onderzoek)}
                  disabled={!onderzoek.is_active || savingId === onderzoek.id}
                >
                  {savingId === onderzoek.id ? "Opslaan…" : "Toegang opslaan"}
                </Button>
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
